import { useEffect, useState } from "react";

import {
  Activity,
  ArrowLeft,
  Building2,
  Calendar,
  ClipboardList,
  Copy,
  Edit,
  IndianRupee,
  MapPin,
  Package2,
  Trash2,
  UserCheck
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import CurrencyDisplay from "../../components/shared/CurrencyDisplay";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import TruncatedText from "../../components/shared/TruncatedText";
import { Button } from "../../components/ui/button.jsx";
import { Card, CardContent } from "../../components/ui/card.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs.jsx";
import { useApi } from "../../context/ApiContext";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { formatDate } from "../../utils/dateUtils";

import DuplicateProjectDialog from "./component/DuplicateProjectDialog";
import EditProjectDialog from "./component/EditProjectDialog";
import MilestonesTab from "./component/MilestonesTab";
import NotesTab from "./component/NotesTab";

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { users } = useData();
  const api = useApi();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("milestones");
  const [projectCosts, setProjectCosts] = useState(0);

  // Management states
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      const projectRes = await api.get(`/projects/${id}`);
      const projectData = projectRes.data;
      setProject(projectData);
      setProjectCosts(projectData.consumedBudget ?? 0);
    } catch (error) {
      console.error("Failed to load project details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (updatedProject) => {
    try {
      const data = {
        ...updatedProject,
        startDate: updatedProject.startDate ? new Date(updatedProject.startDate).toISOString() : null,
        estimatedBudget: updatedProject.estimatedBudget ? parseFloat(updatedProject.estimatedBudget) : null
      };

      await api.put(`/projects/${id}`, data);
      toast.success("Project updated successfully!");
      setIsEditOpen(false);
      loadProjectData();
    } catch (error) {
      toast.error(error.message || "Failed to update project");
    }
  };

  const confirm = useConfirm();

  const handleDelete = async () => {
    const isConfirmed = await confirm({
      title: "Delete Project",
      message: "Are you sure you want to delete this project? This action cannot be undone and will delete all associated data."
    });
    if (isConfirmed) {
      try {
        await api.delete(`/projects/${id}`);
        toast.success("Project deleted successfully!");
        navigate("/projects");
      } catch (error) {
        toast.error(error.message || "Failed to delete project");
      }
    }
  };

  const handleDuplicate = async (duplicateData) => {
    try {
      const res = await api.post(`/projects/${id}/duplicate`, duplicateData);

      toast.success("Project duplicated successfully!");
      setIsDuplicateOpen(false);
      // Navigate to the new project
      navigate(`/projects/${res.data.id}`);
    } catch (error) {
      toast.error(error.message || "Failed to duplicate project");
    }
  };

  useEffect(() => {
    if (id) {
      loadProjectData();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Project Not Found</h2>
        <p className="text-slate-500 mb-4">The project you are looking for does not exist or has been removed.</p>
        <Button onClick={() => navigate("/projects")}>Back to Projects</Button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col space-y-6 animate-in fade-in duration-500">
      <PageHeader
        title={project.name}
        subtitle={
          <TruncatedText text={project.description || "Project overview and management"} lines={2} className="text-slate-500" />
        }
        pageIcon={Building2}
        extraActions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/projects")}
              className={`flex items-center gap-2 h-11 rounded-xl pl-4 ${
                user?.permissions?.project?.update || user?.permissions?.project?.create || user?.permissions?.project?.delete
                  ? "pr-4"
                  : "pr-3"
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden lg:inline">Back</span>
            </Button>

            {(user?.permissions?.project?.update || user?.permissions?.project?.create || user?.permissions?.project?.delete) && (
              <div className="h-4 w-[1px] bg-slate-200 dark:bg-slate-800 mx-1 hidden sm:block" />
            )}

            {user?.permissions?.project?.update && !project.deleted && (
              <Button
                variant="edit"
                size="sm"
                onClick={() => setIsEditOpen(true)}
                className="flex items-center gap-2 h-11 rounded-xl px-4"
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            )}

            {user?.permissions?.project?.create && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDuplicateOpen(true)}
                className="flex items-center gap-2 h-11 rounded-xl px-4"
              >
                <Copy className="w-4 h-4" />
                <span className="hidden sm:inline">Duplicate</span>
              </Button>
            )}

            {user?.permissions?.project?.delete && !project.deleted && (
              <Button variant="delete" size="sm" onClick={handleDelete} className="flex items-center gap-2 h-11 rounded-xl px-4">
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Delete</span>
              </Button>
            )}
          </div>
        }
      />

      <EditProjectDialog open={isEditOpen} onOpenChange={setIsEditOpen} project={project} onUpdate={handleUpdate} />

      <DuplicateProjectDialog
        open={isDuplicateOpen}
        onOpenChange={setIsDuplicateOpen}
        project={project}
        onDuplicate={handleDuplicate}
      />

      <div className="flex flex-col gap-6">
        {/* Project Info Card - Now aligned with standard card styling */}
        <Card className="shadow bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm overflow-hidden border-slate-200 dark:border-slate-800">
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-yale-blue-50 dark:bg-yale-blue-900/40 text-yale-blue-600 dark:text-yale-blue-400">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Location</p>
                  <TruncatedText
                    text={project.location || "Not specified"}
                    lines={1}
                    className="text-sm font-semibold text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>

              <div className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Start Date</p>
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{formatDate(project.startDate)}</p>
                </div>
              </div>

              <div className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                  <IndianRupee className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Estimated Budget</p>
                  <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    <CurrencyDisplay amount={project.estimatedBudget} />
                  </div>
                </div>
              </div>

              <div className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-orange-50 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400">
                  <IndianRupee className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Consumed Budget</p>
                  <div className="text-sm font-bold text-orange-600 dark:text-orange-400">
                    <CurrencyDisplay amount={projectCosts} />
                  </div>
                </div>
              </div>

              <div className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-xl bg-yale-blue-50 dark:bg-yale-blue-900/40 text-yale-blue-600 dark:text-yale-blue-400">
                  <Activity className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</p>
                  <div className="mt-1">
                    <StatusBadge status={project.status} />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Detailed Tabs & Parallel Quick Links */}
        <div className="w-full">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <TabsList className="bg-slate-100/50 dark:bg-slate-800/50 p-1 rounded-xl w-full md:w-auto min-w-[240px]">
                {user?.permissions?.milestone?.read && (
                  <TabsTrigger value="milestones" className="flex-1 rounded-lg py-2">
                    Milestones
                  </TabsTrigger>
                )}
                {user?.permissions?.notes?.read && (
                  <TabsTrigger value="notes" className="flex-1 rounded-lg py-2">
                    Notes
                  </TabsTrigger>
                )}
              </TabsList>

              <div className="flex items-center gap-2 bg-white/40 dark:bg-slate-900/40 backdrop-blur-sm p-1 rounded-xl border border-slate-200/50 dark:border-slate-800/50 shadow-sm overflow-x-auto no-scrollbar">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-[11px] font-bold hover:bg-yale-blue-50 dark:hover:bg-yale-blue-900/40 hover:text-yale-blue-600 dark:hover:text-yale-blue-400 transition-all gap-1.5 shrink-0"
                  onClick={() => navigate(`/tasks?projectId=${id}`)}
                >
                  <ClipboardList className="w-3.5 h-3.5" />
                  Tasks
                </Button>
                <div className="w-[1px] h-3 bg-slate-200 dark:bg-slate-800 shrink-0" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-[11px] font-bold hover:bg-orange-50 dark:hover:bg-orange-900/40 hover:text-orange-600 dark:hover:text-orange-400 transition-all gap-1.5 shrink-0"
                  onClick={() => navigate(`/activities?projectId=${id}`)}
                >
                  <Activity className="w-3.5 h-3.5" />
                  Activities
                </Button>
                <div className="w-[1px] h-3 bg-slate-200 dark:bg-slate-800 shrink-0" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-[11px] font-bold hover:bg-emerald-50 dark:hover:bg-emerald-900/40 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all gap-1.5 shrink-0"
                  onClick={() => navigate(`/materials?projectId=${id}`)}
                >
                  <Package2 className="w-3.5 h-3.5" />
                  Materials
                </Button>
                <div className="w-[1px] h-3 bg-slate-200 dark:bg-slate-800 shrink-0" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-3 text-[11px] font-bold hover:bg-purple-50 dark:hover:bg-purple-900/40 hover:text-purple-600 dark:hover:text-purple-400 transition-all gap-1.5 shrink-0"
                  onClick={() => navigate(`/attendance?projectId=${id}`)}
                >
                  <UserCheck className="w-3.5 h-3.5" />
                  Attendance
                </Button>
              </div>
            </div>

            {user?.permissions?.milestone?.read && (
              <TabsContent value="milestones" className="mt-0 outline-none">
                <MilestonesTab selectedProject={project} />
              </TabsContent>
            )}

            {user?.permissions?.notes?.read && (
              <TabsContent value="notes" className="mt-0 outline-none">
                <NotesTab selectedProject={project} user={user} users={users} />
              </TabsContent>
            )}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
